import { Routes, Route, useLocation } from 'react-router-dom';
import { ROUTES_PATH } from '../constants';
import { MainContextProvider } from '../contexts';
import {
  End,
  Entry,
  LaborData,
  PersonalData,
  Signature,
  Transition,
  Timeout,
  Denial,
  UnknownError,
  Continuation,
  PersonalFinancesUnavailable,
  Documentation,
} from '../pages';
import { useEffect, useState } from 'react';
import ErrorMsg from '../components/ErrorMsg/ErrorMsg';
import Unprocessable from '../pages/Unprocessable/Unprocessable';
import { Quit } from './../pages/Quit/Quit';
import { OperationShipping } from '../pages/OperationShipping/OperationShipping';
import { Landing } from '../pages/Landing';
import { OperationShippingFinished } from '../pages/OperationShippingFinished';
import { ExistingPaymentSelector } from '../pages/ExistingPaymentSelector';
import InsightService from '../services/Insight';
import Bankreader from '../pages/Bankreader/Bankreader';
import { EntrySUS } from '../pages/EntrySUS';
import { ExistingRefinancedLoan } from '../pages/ExistingRefinancedLoan/ExistingRefinancedLoan';
import { PersonalFinances } from '../pages/PersonalFinances/PersonalFinances';
import { Payment } from '../pages/Payment';
import { CounterOffer } from '../pages/CounterOffer';
import { Cancel } from '../pages/Cancel';

interface RouterProps {
  closeButton: (data: boolean) => void;
  removeArrow: (data: boolean) => void;
  bankreaderArrow: (data: boolean) => void;
  iframeResize: (data?: any) => void;
  greenCardOverlayEvent: (data: boolean) => void;
}

const AppRoutes: React.FC<RouterProps> = ({
  closeButton,
  removeArrow,
  iframeResize,
  bankreaderArrow,
  greenCardOverlayEvent,
}) => {
  const { pathname, state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentData, setpaymentData] = useState<string>('');

  const getLoadingEvent = (data: boolean) => {
    setLoading(data);
    if (isSUSPersonalData()) {
      removeArrow(true);
    } else {
      removeArrow(data);
    }
  };

  // Hide quit button
  const getClosingEvent = (data: boolean) => {
    setClosing(data);
  };

  const getThirdParty = (data: string) => {
    setpaymentData(data);
  };

  const getReturnEvent = (data: any) => {
    bankreaderArrow(data);
  };

  const showError = (errorCode: string) => {
    setErrorMessage(errorCode);
    setTimeout(() => {
      setErrorMessage('');
    }, 3000);
  };

  const isSUSPersonalData = () => {
    return (
      pathname === ROUTES_PATH.PERSONAL_DATA &&
      sessionStorage.getItem('Flow-SUS') === 'true'
    );
  };

  // const appHeight = () => {
  //   const doc = document.documentElement
  //   doc.style.setProperty('--app-height', `${window.innerHeight}px`)
  // }

  // Scroll to top if path changes
  useEffect(() => {
    document.getElementById('modal-bg')!.scrollTo(0, 0);
    if (window) {
      window.scrollTo(0, 0);
      InsightService.logPageView(pathname);
      if (pathname === ROUTES_PATH.BANKREADER) {
        iframeResize();
      }
    }

    if (isSUSPersonalData()) {
      removeArrow(true);
    }
    // window.addEventListener('resize', appHeight)
    // appHeight()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const details: any = state;
    if (details?.errorCode) {
      showError(details.errorCode);
    }
  }, [state]);

  useEffect(() => {
    if (closing === false) {
      closeButton(false);
    }
  }, [closing, closeButton]);

  return (
    <>
      {loading && <Transition />}
      <MainContextProvider>
        {errorMessage && <ErrorMsg errorCode={errorMessage} />}
        <Routes>
          <Route path={ROUTES_PATH.LANDING} element={<Landing />} />
          <Route path={ROUTES_PATH.ENTRY} element={<Entry />} />
          <Route
            path={ROUTES_PATH.ENTRY_SUS}
            element={<EntrySUS loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.PERSONAL_DATA}
            element={<PersonalData loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.LABOR_DATA}
            element={<LaborData loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.OCR}
            element={
              <Documentation
                loadingEvent={getLoadingEvent}
                closingEvent={getClosingEvent}
                helpOverlayEvent={greenCardOverlayEvent}
              />
            }
          />
          {/* <Route
            path={ROUTES_PATH.PAYMENT}
            element={
              <Tpv
                paymentThirdParty={getThirdParty}
                loadingEvent={getLoadingEvent}
              />
            }
          /> */}
          <Route
            path={ROUTES_PATH.PAYMENT}
            element={
              <Payment
                loadingEvent={getLoadingEvent}
                closingEvent={getClosingEvent}
              />
            }
          />
          <Route
            path={ROUTES_PATH.BANKREADER}
            element={
              <Bankreader
                loadingEvent={getLoadingEvent}
                returnEvent={getReturnEvent}
              />
            }
          />
          <Route
            path={ROUTES_PATH.SIGNATURE}
            element={
              <Signature
                paymentThirdParty={paymentData}
                loadingEvent={getLoadingEvent}
                closingEvent={getClosingEvent}
                signatureError={(error: string) => showError(error)}
              />
            }
          />
          <Route
            path={ROUTES_PATH.END}
            element={<End loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.TIMEOUT}
            element={<Timeout loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.DENIAL}
            element={<Denial loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.CANCEL}
            element={<Cancel loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.UNKNOWN_ERROR}
            element={<UnknownError loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.UNPROCESSABLE}
            element={<Unprocessable loadingEvent={getLoadingEvent} />}
          />
          <Route path={ROUTES_PATH.QUIT} element={<Quit />} />
          <Route
            path={ROUTES_PATH.OPERATION_SHIPPING}
            element={<OperationShipping loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.OPERATION_SHIPPING_FINISHED}
            element={<OperationShippingFinished />}
          />
          <Route
            path={ROUTES_PATH.CONTINUATION}
            element={<Continuation loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.EXISTING_PAYMENT}
            element={<ExistingPaymentSelector loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.EXISTING_RFN}
            element={<ExistingRefinancedLoan loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.PERSONAL_FINANCES}
            element={<PersonalFinances loadingEvent={getLoadingEvent} />}
          />
          <Route
            path={ROUTES_PATH.PERSONAL_FINANCES_UNAVAILABLE}
            element={
              <PersonalFinancesUnavailable loadingEvent={getLoadingEvent} />
            }
          />
          <Route
            path={ROUTES_PATH.COUNTER_OFFER}
            element={<CounterOffer loadingEvent={getLoadingEvent} />}
          />
        </Routes>
      </MainContextProvider>
    </>
  );
};

export default AppRoutes;
